import type { MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router';

import { cn } from '@shared/ui/cn';

/**
 * The two action shapes the system pair draws, at the large size, in the design
 * system's own recipe.
 *
 * They live in this feature rather than in `@shared/ui/` because these two pages
 * are the only surface in the console that draws a large button: every other
 * committing action sits inside a table, a dialog, or a form at the default
 * size, and the top bar's create action is this area's one primary button. The
 * recipe is promoted the first time a second feature needs it, not before.
 *
 * **One primary per view.** The system pages are the exception the rule allows
 * for: there is no content column behind them holding a competing primary, and
 * the page exists to offer a way out.
 */
const BASE = cn(
  'inline-flex min-h-[3.25rem] w-full items-center justify-center gap-2 rounded-sm border',
  'px-6 py-4 text-body font-semibold leading-[1.2] no-underline',
  'active:translate-y-px transition-[background-color,border-color,color] duration-fast ease-standard',
  // Full width at the base tier, then side by side: two large buttons sharing a
  // 320 px line fall below the 44 px target.
  'sm:w-auto',
);

const INTENT = {
  primary: 'border-accent bg-accent text-accent-ink hover:bg-accent-hover active:bg-accent-press',
  secondary: 'border-border-strong bg-surface-raised text-ink hover:bg-surface-overlay',
} as const;

export type SystemActionIntent = keyof typeof INTENT;

/** A route out of the failure — always an internal console destination. */
export function SystemLinkAction({
  to,
  intent,
  icon,
  children,
}: {
  to: string;
  intent: SystemActionIntent;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <Link to={to} className={cn(BASE, INTENT[intent])}>
      {icon}
      <span>{children}</span>
    </Link>
  );
}

/**
 * The retry, and the reason it is an anchor rather than a button: without
 * scripting the element still re-requests the document, so the page's primary
 * action is never a dead control. With scripting the handler upgrades it to a
 * real reload, which re-requests **the URL that failed** rather than the error
 * page's own — in production this markup is served at that URL.
 *
 * A client-side navigation would be the wrong verb entirely: it would re-render
 * the same failed view out of the same cache without asking anyone anything.
 */
export function SystemReloadAction({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  function onActivate(event: MouseEvent<HTMLAnchorElement>) {
    // Let a modifier-click open a new tab the way the reader asked.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    event.preventDefault();
    window.location.reload();
  }

  return (
    <a
      data-reload
      href={window.location.pathname + window.location.search}
      onClick={onActivate}
      className={cn(BASE, INTENT.primary)}
    >
      {icon}
      <span>{children}</span>
    </a>
  );
}
